<script setup lang="ts">
import { formatNumber } from "../../../services/Utils/DecimalFormatter";
import timeAgo from "../../../services/Utils/timeAgo";

import { canEditProjectRole, canManageProjectRole, VIEWER_READ_ONLY_HINT } from "@/providers/Permissions/projectRoleAccess";
import { materialMappingMenuLabel } from "../utils/materialMappingMenuLabel";
import { GetProjectListQuery } from "@/graphql";
import { EmissionBudgetState } from "@/api/composables/useEmissionBudgets.composable";
import { getProjectUsedEmission } from "@/services/Utils/emissionBudget";
import Popover from "@/components/Reusables/Popover/Popover.vue";
import CheckBox from "@/components/Reusables/Inputs/CheckBox.vue";
import InviteModalNew from "@/components/Utils/InviteUserToProject/InviteModalNew.vue";
import ProgressBar from "@/components/Reusables/Loading/ProgressBar.vue";
import { Bookmark, EllipsisVertical } from "@lucide/vue";
import { computed, nextTick, ref } from "vue";
import type { ProjectCompareItem } from "../composables/useProjectSelection";

const props = defineProps<{
  emissionBudget?: EmissionBudgetState;
  isSelectedForBulk?: boolean;
  project: GetProjectListQuery["project"][0];
}>();

const emit = defineEmits<{
  (e: "compareItem", item: ProjectCompareItem): void;
}>();

// Toggle this row in/out of the compare set (mirrors LibraryMaterial's per-card compare checkbox).
const emitCompare = () => {
  emit("compareItem", { id: props.project.id, name: props.project.name });
};

// The current revision (named save point) travels with the projectList fragment, so
// the list query AND the ProjectListUpdated subscription keep it live. Only its NAME is shown as a
// chip: the row title is the project's live name, because the current revision's state IS the
// working state — it holds no captured project name of its own (RTLCA-5270).
const activeRevisionName = computed(() => props.project.activeRevision?.name ?? "");
const displayName = computed(() => props.project.name);

// What this user may do with THIS project, from the membership role that travels on the row — the
// same guard the grid card uses, because the view toggle must not be a way around the lock. The
// mapping entry is renamed rather than hidden: the page is readable with Viewer access, only the
// promise in the label was wrong (RTLCA-5583).
const canEditProject = computed(() => canEditProjectRole(props.project.role));
const canManageProject = computed(() => canManageProjectRole(props.project.role));
const materialMappingLabel = computed(() => materialMappingMenuLabel(canEditProject.value));

const materialMappingTooltip = computed(() => ({
  text: VIEWER_READ_ONLY_HINT,
  variant: "label" as const,
  placement: "bottom" as const,
  // Suppressed rather than unbound, so an Admin's menu never explains a control they can fully use.
  disabled: canEditProject.value,
}));

// Resolved the same way as the grid card. Reading `budget.used.value` straight ignores that a BR18
// 2025 project with special conditions is measured on its special-condition total, so the same
// project showed one number as a card and another as a row.
const usedEmission = computed(() => {
  const budget = props.emissionBudget?.data?.budget;
  return budget ? getProjectUsedEmission(props.project, budget) : null;
});

// Undefined until the row's budget is requested, `loading` while in flight; a settled row without one shows "-".
const isBudgetPending = computed(() => !props.emissionBudget || props.emissionBudget.loading);

// Per project, from the field the grid card reads too; the header no longer states one unit for every row (RTLCA-5612).
const emissionUnitLabel = computed(() => props.emissionBudget?.data?.unit?.label ?? "");

// Share of building elements mapped, shown as a bar rather than a bare number (RTLCA-5610): a row of
// percentages says nothing at a glance, where the bar's length and its coverage colour ramp do.
// ProgressBar owns its own loading look, so the cell no longer needs a skeleton of its own.
const isMappingLoading = computed(() => !props.emissionBudget?.data?.mapping?.material);
const mappingPercentage = computed(() => props.emissionBudget?.data?.mapping?.material?.percentage ?? 0);

const menuItemClass =
  "focus-ring rounded-4 t-small text-type-default flex w-full items-center px-3 py-2 text-left transition-colors hover:bg-slate-100";

// The member dialog the grid card's menu opens too (RTLCA-5806): the row menu was the only entry
// point on this page that did not offer it, so switching to list view took user management away.
const inviteUsersModalIsHidden = ref<boolean>(true);

const toggleInviteUsersModal = () => {
  inviteUsersModalIsHidden.value = !inviteUsersModalIsHidden.value;
};

// Close the menu, then open the dialog a tick later. Popover returns focus to its trigger on the
// next tick and Modal remembers whatever is focused as it opens, so opening the dialog immediately
// makes it remember this menu item — which the closing panel takes with it, leaving focus on the
// body when the dialog is dismissed. Waiting one tick lets the trigger take focus first, so the
// dialog hands it back there.
const openManageUsers = async (closeMenu: () => void) => {
  closeMenu();
  await nextTick();
  toggleInviteUsersModal();
};
</script>

<template>
  <div
    role="row"
    :class="[
      'text-type-default duration-fast ease-standard grid cursor-pointer grid-cols-17 items-center gap-2 border-b border-slate-200 px-4 py-2 transition-colors last:border-b-0 hover:bg-slate-50',
      isSelectedForBulk ? '!border-app-blue-600 bg-app-blue-600/5' : 'bg-slate-0',
    ]">
    <!-- Compare: its own column rather than sharing the name cell, where it took half the width and
         truncated the project name. The word "Compare" now sits in the header once instead of on every
         row; per-row the accessible name comes from the checkbox's aria-label.

         The whole row is wrapped in a real <a href> (ProjectList.vue), so a click here would open the
         project. Two handlers are needed, and the capture one is the load-bearing part (RTLCA-5117):
         CheckBox's inner box calls stopPropagation on its own click, so a bubble-phase handler here
         never runs for the most common click of all. Capture fires before the target, so .prevent
         always lands; vue-router's guardEvent then bails because defaultPrevented is set. No .stop in
         capture — that would swallow the click before CheckBox could toggle. -->
    <span class="col-span-1 flex items-center">
      <span
        class="rounded-4 duration-fast ease-standard flex size-7 shrink-0 items-center justify-center transition-colors hover:bg-slate-200"
        @click.capture.prevent
        @click.stop>
        <!-- CheckBox passes an unrecognised `color` straight through as a class. The shared "input"
             default is border-slate-200, which nearly vanishes against the slate-0 row now that the box
             stands alone in its own column. Measured against that row: slate-200 is 1.2:1 and slate-400
             only 2.56:1, both under the 3:1 WCAG 1.4.11 non-text minimum; slate-500 measures 4.8:1. -->
        <CheckBox
          size="lg"
          color="border-slate-500"
          aria-label="Add to comparison"
          :value="!!isSelectedForBulk"
          @update:value="emitCompare" />
      </span>
    </span>

    <span class="col-span-3 flex min-w-0 flex-col">
      <span class="t-small-bold text-type-default truncate">{{ displayName }}</span>
      <!-- The current revision — the save point everyone on the project works on. Always shown,
           matching the grid card: `project.active_revision_id` is NOT NULL, and a project's first
           revision is named "Initial revision" by the Hasura `project_ensure_active_revision`
           trigger (the project's own name goes to a separate column), so the chip names a real save
           point from the moment the project exists (RTLCA-5536). -->
      <span
        v-if="activeRevisionName"
        v-tooltip="{ text: `Current revision: ${activeRevisionName}`, variant: 'label' }"
        class="gap-px-4 t-footnote text-app-blue-900 flex min-w-0 items-center"
        data-cy="project-active-revision">
        <Bookmark
          class="icon-xs shrink-0"
          :stroke-width="1.5"
          color="var(--color-app-blue-800)"
          fill="var(--color-app-blue-100)" />
        <span class="truncate">{{ activeRevisionName }}</span>
      </span>
    </span>

    <span class="text-type-muted col-span-2">
      {{ timeAgo(project.updatedAt || project.createdAt) }}
    </span>

    <!-- Mapping complete. ProgressBar formats the value it hands the label, so the figure still reads
         as e.g. 77.78% rather than the raw float. `pr-4` keeps the bar off the Threshold column, which
         the 8px grid gap alone left it flush against. -->
    <span class="col-span-2 pr-4">
      <ProgressBar
        :label="(percentage) => `${percentage}%`"
        :value="mappingPercentage"
        :loading="isMappingLoading"
        aria-label="Mapping progress" />
    </span>

    <!-- GWP budget. Unit under the figure: inline it wraps mid-unit in this narrow column (RTLCA-5612). -->
    <span class="text-type-muted col-span-2 flex min-w-0 flex-col">
      <!-- formatNumber for the same reason the mapping percentage above needs it: the raw value is a
           float and rendered unformatted as e.g. 6.699999999999999. -->
      <template v-if="emissionBudget?.data?.budget">
        <span class="truncate">{{ formatNumber(emissionBudget.data.budget.threshold) }}</span>
        <span
          v-if="emissionUnitLabel"
          v-tooltip.truncated="emissionUnitLabel"
          class="t-footnote truncate">
          {{ emissionUnitLabel }}
        </span>
      </template>
      <span
        v-else-if="isBudgetPending"
        class="skeleton w-16"></span>
      <span v-else>-</span>
    </span>

    <!-- GWP used -->
    <span class="text-type-muted col-span-2 flex min-w-0 flex-col">
      <template v-if="usedEmission">
        <span class="truncate">{{ formatNumber(usedEmission.value || 0) }}</span>
        <span
          v-if="emissionUnitLabel"
          v-tooltip.truncated="emissionUnitLabel"
          class="t-footnote truncate">
          {{ emissionUnitLabel }}
        </span>
      </template>
      <span
        v-else-if="isBudgetPending"
        class="skeleton w-14"></span>
      <span v-else>-</span>
    </span>

    <!-- Total area -->
    <span class="text-type-muted col-span-2"> {{ project.area || 0 }} m&#178; </span>

    <!-- Building type (always present). Truncated: the Finnish types run to a sentence, and
         unclipped this cell wrapped to four lines and took the row from 53px to 112px, so one
         project stood twice as tall as the rest of the table (RTLCA-5807). -->
    <span
      v-tooltip.truncated="project.buildingType.label"
      class="text-type-muted col-span-2 truncate">
      {{ project.buildingType.label }}
    </span>

    <!-- Row actions. The panel stays in flow (not teleported) so Tab moves from the trigger into the
         items; ProjectTable's frame must not clip for that. The wrapper's .prevent.stop keeps every
         click in here from following the row's own link. -->
    <div
      class="mr-2 ml-auto flex h-fit w-fit items-center"
      @click.prevent.stop>
      <Popover
        placement="bottom-end"
        role="menu"
        panel-class="w-48 p-1"
        aria-label="Project actions">
        <template #trigger="{ toggle, isOpen }">
          <button
            type="button"
            class="focus-ring interactive-active rounded-4 duration-fast ease-standard flex size-7 shrink-0 items-center justify-center text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700"
            :class="{ 'bg-slate-200 text-slate-700': isOpen }"
            :aria-label="`Actions for ${displayName}`"
            aria-haspopup="menu"
            :aria-expanded="isOpen"
            @click="toggle">
            <!-- stroke-width 2, as on the grid card's trigger: the dots read as faint rings at 1.5 -->
            <EllipsisVertical
              class="icon-md"
              :stroke-width="2" />
          </button>
        </template>
        <template #default="{ close }">
          <RouterLink
            v-tooltip="materialMappingTooltip"
            role="menuitem"
            :to="{ name: 'materialMappingView', params: { id: project.id } }"
            :class="menuItemClass"
            @click="close">
            {{ materialMappingLabel }}
          </RouterLink>
          <RouterLink
            v-if="canEditProject"
            role="menuitem"
            :to="{ name: 'projectSettings', params: { id: project.id } }"
            :class="menuItemClass"
            @click="close">
            Project settings
          </RouterLink>
          <!-- A dialog rather than a destination, so this entry is a button — gated on the same
               membership role as the grid card's, so the view toggle is not a way around the lock. -->
          <button
            v-if="canManageProject"
            type="button"
            role="menuitem"
            :class="menuItemClass"
            @click="openManageUsers(close)">
            Manage users
          </button>
        </template>
      </Popover>

      <!-- Kept inside this wrapper so the row stays a single root element (the parent hands it a
           class, which a fragment cannot inherit). Modal teleports to `#modal`, so it renders
           outside the row's own <a> and neither the row's link nor this wrapper's click guard
           ever sees a click made inside the dialog. -->
      <InviteModalNew
        v-if="!inviteUsersModalIsHidden"
        :project="project"
        @toggle-invite-modal="toggleInviteUsersModal" />
    </div>
  </div>
</template>

<style scoped>
@reference "../../../style.css";

.skeleton {
  @apply inline-block h-4 animate-pulse rounded bg-slate-200 align-middle;
}
</style>
